import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useHttp from '../../../hooks/http.hook';
import { rewardsFromDiscountMap } from '../../../utils/productGroups';
import CreateBusinessHeader from '../../business/components/CreateBusinessHeader';
import CreateFooter from '../../business/components/CreateFooter';
import PromotionNameImageForm from '../components/PromotionNameImageForm';
import DeletePromotionButton from '../components/DeletePromotionButton';
import PromotionProductsPicker, {
  PromotionPickerOpenButton,
} from '../components/PromotionProductsPicker';
import { createConditionalDiscountPromotion } from '../handlers/conditionalDiscountPromotion';
import '../../business/Business.css';
import './CreateConditionalDiscountPromotionPage.css';

function CreateConditionalDiscountPromotionPage() {
  const navigate = useNavigate();
  const { promotionId } = useParams();
  const { requestWithMeta } = useHttp();
  const isEdit = !!promotionId;

  const [name, setName] = React.useState('');
  const [imageUrl, setImageUrl] = React.useState('');
  const [isImageUploading, setIsImageUploading] = React.useState(false);
  const [minValue, setMinValue] = React.useState('');
  const [promotionLoading, setPromotionLoading] = React.useState(false);
  const [discounts, setDiscounts] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState(null);
  const [openPicker, setOpenPicker] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    if (!isEdit) return;

    setPromotionLoading(true);
    setSubmitError(null);

    (async () => {
      try {
        const res = await requestWithMeta(`/operator/promotions/${encodeURIComponent(promotionId)}`, 'GET');
        if (cancelled) return;
        if (!res?.ok) {
          setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось загрузить акцию');
          return;
        }
        const promo = res?.data ?? {};
        setName(promo?.name ?? '');
        setImageUrl(promo?.imageUrl ?? promo?.image_url ?? '');

        const conditions = Array.isArray(promo?.conditions) ? promo.conditions : [];
        const cond = conditions[0] || {};
        const min = cond?.metaData?.minValue ?? cond?.meta_data?.minValue ?? '';
        if (min !== '' && min !== null && min !== undefined) setMinValue(String(min));

        const rewards = Array.isArray(promo?.rewards) ? promo.rewards : [];
        const nextDiscounts = {};
        rewards.forEach((r) => {
          const pid = r?.productId ?? r?.product_id;
          const d = r?.discount;
          if (pid == null) return;
          if (d === null || d === undefined) return;
          const n = Number(d);
          if (!Number.isFinite(n)) return;
          nextDiscounts[pid] = n;
        });
        setDiscounts(nextDiscounts);
      } catch (e) {
        if (!cancelled) setSubmitError(e?.message || 'Не удалось загрузить акцию');
      } finally {
        if (!cancelled) setPromotionLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [isEdit, promotionId, requestWithMeta]);

  const handleDiscountChange = (productId, value) => {
    const num = value === '' ? '' : Math.min(100, Math.max(0, Number(value) || 0));
    setDiscounts((prev) => ({ ...prev, [productId]: num }));
  };

  const rewardsWithDiscount = React.useMemo(
    () => rewardsFromDiscountMap(discounts),
    [discounts]
  );

  const numericMinValue = React.useMemo(() => {
    const normalized = String(minValue).replace(',', '.').trim();
    if (!normalized) return NaN;
    const n = Number(normalized);
    if (!Number.isFinite(n)) return NaN;
    return Math.max(0, n);
  }, [minValue]);

  const canSubmit =
    name.trim().length > 0 &&
    Number.isFinite(numericMinValue) &&
    numericMinValue > 0 &&
    rewardsWithDiscount.length > 0 &&
    !submitting &&
    !isImageUploading &&
    !promotionLoading;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const res = isEdit
        ? await requestWithMeta(`/operator/promotions/${encodeURIComponent(promotionId)}`, 'PUT', {
            name: name.trim(),
            imageUrl: imageUrl.trim() || null,
            conditions: [
              { typeId: 1, metaData: { minValue: numericMinValue } }
            ],
            rewards: rewardsWithDiscount
          })
        : await createConditionalDiscountPromotion(requestWithMeta, {
            name: name.trim(),
            imageUrl: imageUrl.trim() || null,
            minValue: numericMinValue,
            rewards: rewardsWithDiscount,
          });
      if (res.ok) {
        navigate('/promotions');
        return;
      }
      setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось создать акцию');
    } catch (e) {
      setSubmitError(e?.message || 'Ошибка при создании акции');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (openPicker) {
    return (
      <PromotionProductsPicker
        title="Скидки по товарам"
        subtitle="Укажите скидку по каждому товару"
        mode="discount"
        discounts={discounts}
        onDiscountChange={handleDiscountChange}
        onClose={() => setOpenPicker(false)}
      />
    );
  }

  return (
    <div className="create-cond-disc-promo-root">
      <CreateBusinessHeader
        title={'Покупка на сумму + скидки'}
        subtitle="Скидки по товарам при сумме от X"
        onBack={handleBack}
      />

      <main className="create-cond-disc-promo-content">
        <PromotionNameImageForm
          name={name}
          onNameChange={setName}
          imageUrl={imageUrl}
          onImageUrlChange={setImageUrl}
          onImageUploadLoadingChange={setIsImageUploading}
        />

        <section className="shop-create-section">
          <label className="shop-create-label" htmlFor="cond-disc-min-value">
            Минимальная сумма заказа (сом) <b style={{ color: 'red' }}>*</b>
          </label>
          <input
            id="cond-disc-min-value"
            className="shop-create-input"
            placeholder="Например, 1000"
            value={minValue}
            onChange={(e) => setMinValue(e.target.value)}
            autoComplete="off"
            inputMode="decimal"
          />
        </section>

        <section className="shop-create-section">
          <PromotionPickerOpenButton
            title="Скидки по товарам"
            hint={
              rewardsWithDiscount.length > 0
                ? `Выбрано: ${rewardsWithDiscount.length}`
                : 'Указать скидки'
            }
            onClick={() => setOpenPicker(true)}
          />
        </section>

        {submitError ? (
          <div className="create-cond-disc-promo-error">{submitError}</div>
        ) : null}
      </main>

      <CreateFooter
        disabled={!canSubmit}
        onCreate={handleSubmit}
        label={
          submitting
            ? isEdit
              ? 'Сохранение…'
              : 'Создание…'
            : isEdit
              ? 'Сохранить'
              : 'Создать акцию'
        }
        leading={
          isEdit ? (
            <DeletePromotionButton
              promotionId={promotionId}
              promotionName={name}
              requestWithMeta={requestWithMeta}
              disabled={submitting}
              onDeleted={() => navigate('/promotions')}
            />
          ) : null
        }
      />
    </div>
  );
}

export default CreateConditionalDiscountPromotionPage;

